/* eslint-disable jsx-a11y/label-has-associated-control */
import { useEffect, useState, type FunctionComponent } from 'react';
import get from 'lodash/get';
import size from 'lodash/size';
import startCase from 'lodash/startCase';
import toLower from 'lodash/toLower';
import { useForm } from 'react-hook-form';
import { track } from '@fff-web/fff-analytics';
import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import { FFFStorage } from '@fff-web/fff-utilities';
import { updatePassword, updateProfileInfo } from '../services/api/account';
import AdminInput from '../shared-components/AdminInput/AdminInput';
import Button, { BUTTON_TYPES } from '../shared-components/Button/Button';
import { ACCOUNT_INFO, ACCOUNT_SECTIONS, ACCOUNT_TRACK_NAMES } from '../utils/segment';
import * as styles from './Section.module.css';

const EMAIL_CHARACTER_LIMIT = '101';

const BUTTON_CTAS = {
  save: 'SAVE',
  cancel: 'Cancel',
  edit: 'Edit',
};

const getInfoUpdated = (dirtyFields: Record<string, boolean>) => {
  const { firstName, lastName, email } = dirtyFields;
  const fields = [];
  if (firstName || lastName) {
    fields.push(ACCOUNT_INFO.name);
  }
  if (email) {
    fields.push(ACCOUNT_INFO.email);
  }
  return fields;
};

type PersonalInformationProps = {
  firstName: string;
  lastName: string;
  email: string;
  updateUserInfo: (data: Record<string, string>) => void;
};

const PersonalInformation: FunctionComponent<PersonalInformationProps> = ({
  firstName,
  lastName,
  email,
  updateUserInfo,
}: PersonalInformationProps) => {
  const defaultValues = {
    firstName: startCase(toLower(firstName)),
    lastName: startCase(toLower(lastName)),
    email: toLower(email),
    currentPassword: '',
    newPassword: '',
    confrimPassword: '',
  };
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, dirtyFields },
  } = useForm({
    defaultValues,
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittedData, setSubmittedData] = useState({});
  const [isPasswordSubmitSuccessful, setPasswordSubmitSuccessful] = useState<boolean | undefined>();
  const [isProfileSubmitedSuccessful, setProfileSubmitSuccessfull] = useState<
    boolean | undefined
  >();

  const getUpdatedData = (data: Record<string, string>, isUpdatePassword?: boolean) => {
    if (isUpdatePassword) {
      return {
        oldPassword: data.currentPassword,
        newPassword: data.newPassword,
      };
    }
    return {
      firstName: dirtyFields.firstName && data.firstName,
      lastName: dirtyFields.lastName && data.lastName,
      email: dirtyFields.email && data.email,
    };
  };

  const toggleOpenSection = () => {
    setOpen((prevState) => {
      const newState = !prevState;
      if (newState) reset(defaultValues);
      return newState;
    });
  };

  const handleClickEdit = () => {
    if (!open) {
      track({
        trackName: ACCOUNT_TRACK_NAMES.started,
        properties: {
          cta_name: BUTTON_CTAS.edit,
          account_section: ACCOUNT_SECTIONS.personal,
        },
      });
    }
    toggleOpenSection();
  };

  const updateSessionStorage = (value: Record<string, string>) => {
    const userInfo = FFFStorage.getFromStorage({
      key: FFFStorage.SESSION_STORAGE_KEY.userInfo,
    });
    const updatedUserInfo = {
      ...userInfo,
      user: {
        ...userInfo.user,
        ...value,
      },
    };
    FFFStorage.setToStorage({
      key: FFFStorage.SESSION_STORAGE_KEY.userInfo,
      value: updatedUserInfo,
    });
    // dispatch event to update UserContext
    window.dispatchEvent(new Event(FFFStorage.SESSION_STORAGE_EVENT));
  };

  const onSubmit = async (data: Record<string, string>) => {
    const isUserInfoChanged = dirtyFields.firstName || dirtyFields.lastName || dirtyFields.email;
    try {
      if (data.newPassword && data.currentPassword) {
        setLoading(true);
        const newdata = getUpdatedData(data, true);
        const response = await updatePassword(newdata);
        if (response.success) {
          setSubmittedData(data);
          setPasswordSubmitSuccessful(true);
          if (!isUserInfoChanged) {
            setLoading(false);
          }
          showSuccessToast('Password Updated', 'Your password change has been successfully saved.');
          toggleOpenSection();
        }
      }
    } catch (_) {
      setPasswordSubmitSuccessful(false);
      if (!isUserInfoChanged) {
        setLoading(false);
      }
      showErrorToast(
        'Failed to Save Password',
        'We were unable to save your password. Please try again.',
      );
    }
    try {
      if (isUserInfoChanged) {
        setLoading(true);
        const newdata = getUpdatedData(data);
        const responseProfile = await updateProfileInfo(newdata);
        if (responseProfile.success) {
          track({
            trackName: ACCOUNT_TRACK_NAMES.completed,
            properties: {
              cta_name: BUTTON_CTAS.save,
              account_section: ACCOUNT_SECTIONS.personal,
              info_updated: getInfoUpdated(dirtyFields),
            },
          });
          setSubmittedData(data);
          setLoading(false);
          setProfileSubmitSuccessfull(true);
          updateSessionStorage(responseProfile.value);
          showSuccessToast(
            'Personal Information Updated',
            'Your personal information changes have been successfully saved.',
          );
          toggleOpenSection();
        }
      }
    } catch (_) {
      setLoading(false);
      setProfileSubmitSuccessfull(false);
      showErrorToast(
        'Failed to Save Profile Information',
        'We were unable to save your profile information. Please try again.',
      );
    }
  };

  useEffect(() => {
    if (isPasswordSubmitSuccessful || isProfileSubmitedSuccessful) {
      reset({
        ...submittedData,
        currentPassword: '',
        newPassword: '',
        confrimPassword: '',
      });
      updateUserInfo(submittedData);
    }
  }, [isPasswordSubmitSuccessful, isProfileSubmitedSuccessful, submittedData, reset]);

  return (
    <>
      <div className={styles.header}>
        <span>Personal Information</span>
        <button type="button" onClick={handleClickEdit} data-testid="edit-btn-personal-information">
          {open ? BUTTON_CTAS.cancel : BUTTON_CTAS.edit}
        </button>
      </div>
      {open ? (
        <div className={styles.editContainer}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.personalForm}>
            <AdminInput
              label="First Name"
              type="text"
              error={errors.firstName && 'This field is required.'}
              datatestid="first-name-fld"
              {...register('firstName', {
                required: true,
                pattern: /^[A-ZÀ-ÿa-z\\s,. '\\pL-]+$/,
              })}
            />
            <AdminInput
              label="Last Name"
              datatestid="last-name-fld"
              type="text"
              error={errors.lastName && 'This field is required.'}
              {...register('lastName', {
                required: true,
                pattern: /^[A-ZÀ-ÿa-z\\s,. '\\pL-]+$/,
              })}
            />
            <AdminInput
              label="Email"
              datatestid="email-fld"
              type="text"
              error={errors.email && errors.email.message}
              characterLimit={EMAIL_CHARACTER_LIMIT}
              {...register('email', {
                required: 'This field is required.',
                pattern: {
                  value:
                    /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
                  message: 'Please enter a valid email address.',
                },
              })}
            />
            <AdminInput
              label="Current Password"
              type="password"
              error={errors.currentPassword && 'Please enter your current password.'}
              {...register('currentPassword', {
                validate: (value) => {
                  if (size(watch('newPassword')) > 0) {
                    return size(value) > 0;
                  }
                  return true;
                },
              })}
            />
            <AdminInput
              label="New Password"
              type="password"
              error={errors.newPassword && get(errors, 'newPassword.message')}
              {...register('newPassword', {
                minLength: {
                  value: 8,
                  message: 'Your password does not meet our minimum character length requirement.',
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
                  message: 'Your password does not meet our requirements listed below.',
                },
                validate: (value) => {
                  if (size(watch('confrimPassword')) > 0 && size(value) === 0) {
                    return 'Your password does not meet our requirements listed below.';
                  }
                  return true;
                },
              })}
            />
            <AdminInput
              label="Confirm Password"
              type="password"
              error={errors.confrimPassword && get(errors, 'confrimPassword.message')}
              {...register('confrimPassword', {
                validate: (value) => value === watch('newPassword') || 'Passwords do not match.',
              })}
            />
            <span>
              Please set a new password for your account that you have not used elsewhere. Choose a
              strong password that has at least 8 characters, with a minimum of 1 number, 1
              uppercase, 1 lowercase, and 1 special character.
            </span>
            <div className={styles.btnContainer}>
              <Button
                buttonType={BUTTON_TYPES.PRIMARY}
                className={styles.save}
                disabled={size(errors) > 0 || size(dirtyFields) === 0 || loading}
                type="submit"
              >
                {BUTTON_CTAS.save}
              </Button>
              <Button
                className={styles.cancel}
                buttonType="cancel"
                onClick={toggleOpenSection}
                style={{ letterSpacing: '0px' }}
              >
                {BUTTON_CTAS.cancel}
              </Button>
            </div>
          </form>
        </div>
      ) : (
        <div className={styles.readOnlyContainer} data-testid="personal-info-section">
          {/* TODO: Please fix the a11y issues with this element and then remove the comments at the top of the file */}
          <label>Name</label>
          <span data-testid="name-title">{`${defaultValues.firstName} ${defaultValues.lastName}`}</span>
          <label>Email & Password</label>
          <span data-testid="email-title">{defaultValues.email}</span>
        </div>
      )}
    </>
  );
};
export default PersonalInformation;
